/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { waitFor } from '@ember/test-waiters';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  booking: ModelRegistry['booking'];
  showCancelActions?: boolean;
}

export interface BookingDetailsSignature {
  Element: null;

  Args: IArgs;
}

export default class UiBookingDetailsComponent extends Component<BookingDetailsSignature> {
  @service notifications!: Services['notifications'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @service('booking-drawer')
  bookingDrawer!: Services['booking-drawer'];

  @tracked isSubmitting = false;

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking']> {
    return this.bookingDrawer.changesetFor(this.booking);
  }

  willDestroy(): void {
    super.willDestroy();

    this.bookingDrawer.hasInvalidInput = false;
  }

  @action
  handleValidityChange(isValid: boolean): void {
    this.bookingDrawer.hasInvalidInput = !isValid;
  }

  @action
  @waitFor
  async handleSaveBooking(): Promise<void> {
    this.isSubmitting = true;

    try {
      this.changeset.set('guestName', this.changeset.rawGuestName);

      const { checkIn, checkOut } = this.booking.changedAttributes() ?? {};

      if (checkIn) {
        this.changeset.set('checkIn', checkIn[0]);
      }

      if (checkOut) {
        this.changeset.set('checkOut', checkOut[0]);
      }

      const { dragOperation, dragChangedMultiUnit } =
        this.calendarManager.interaction;

      if (dragChangedMultiUnit && dragOperation) {
        this.changeset.set('newMultiUnit', dragOperation.location);
      }

      await this.changeset.save();

      this.booking.newMultiUnit = undefined;

      this.notifications.info();

      this.calendarManager.interaction.clearOperations();
    } catch {
      this.notifications.error();
    } finally {
      this.isSubmitting = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Details': typeof UiBookingDetailsComponent;
  }
}
